/**
 * Square Point of Sale (POS) deep-link helpers.
 *
 * Square doesn't expose a sandbox for the mobile-web POS flow — these URLs
 * MUST be tested against a production Square account.
 *
 * iOS scheme  : square-commerce-v1://payment/create?data=<urlencoded JSON>
 * Android URL : intent:#Intent;action=com.squareup.pos.action.CHARGE;...;end
 *
 * Reference: https://developer.squareup.com/docs/pos-api/build-mobile-web
 */

const APPLICATION_ID =
  process.env.REACT_APP_SQUARE_APPLICATION_ID || "";

export function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "unknown";
}

/**
 * Build the iOS deep-link.
 * @param {object} args
 * @param {number} args.amountCents - integer cents (e.g., 1870 for €18.70)
 * @param {string} args.callbackUrl - HTTPS callback URL (must match the
 *                                    Web Callback URL configured in the
 *                                    Square Developer Console)
 * @param {string} args.note         - free-text note (visible in Square)
 * @param {string} args.state        - opaque echo-back string (booking id)
 */
export function buildIOSSquarePosUrl({ amountCents, callbackUrl, note, state }) {
  const data = {
    callback_url: callbackUrl,
    client_id: APPLICATION_ID,
    version: "1.3",
    amount_money: {
      amount: amountCents,
      currency_code: "EUR",
    },
    options: {
      supported_tender_types: ["CREDIT_CARD"],
      clear_default_fees: true,
      auto_return: true,
      skip_receipt: false,
    },
    notes: note,
    state,
  };
  const encoded = encodeURIComponent(JSON.stringify(data));
  return `square-commerce-v1://payment/create?data=${encoded}`;
}

/**
 * Build the Android intent URL.
 */
export function buildAndroidSquarePosIntent({ amountCents, callbackUrl, note }) {
  const fallbackUrl = "https://pricecovoit.fr/square/pos-fallback";
  const parts = [
    "intent:#Intent",
    "action=com.squareup.pos.action.CHARGE",
    "package=com.squareup",
    `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)}`,
    `S.com.squareup.pos.WEB_CALLBACK_URI=${encodeURIComponent(callbackUrl)}`,
    `S.com.squareup.pos.CLIENT_ID=${APPLICATION_ID}`,
    "S.com.squareup.pos.API_VERSION=v2.0",
    `i.com.squareup.pos.TOTAL_AMOUNT=${amountCents}`,
    "S.com.squareup.pos.CURRENCY_CODE=EUR",
    "S.com.squareup.pos.TENDER_TYPES=com.squareup.pos.TENDER_CARD",
    `S.com.squareup.pos.NOTE=${encodeURIComponent(note || "")}`,
    "end",
  ];
  return parts.join(";");
}

/**
 * Universal helper: returns the right URL for the current platform, or
 * `null` if the platform isn't supported (desktop browser).
 */
export function buildSquarePosUrl({ amountCents, callbackUrl, note, state }) {
  const plat = detectPlatform();
  if (plat === "ios") {
    return buildIOSSquarePosUrl({ amountCents, callbackUrl, note, state });
  }
  if (plat === "android") {
    return buildAndroidSquarePosIntent({ amountCents, callbackUrl, note });
  }
  return null;
}

/**
 * Square app install links per platform (App Store / Play Store).
 * Used when the deep-link doesn't resolve (app not installed).
 */
export const SQUARE_APP_STORE_URL =
  "https://apps.apple.com/app/square-point-of-sale-pos/id335393788";
export const SQUARE_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.squareup";

export function getSquareStoreUrl() {
  const plat = detectPlatform();
  if (plat === "ios") return SQUARE_APP_STORE_URL;
  if (plat === "android") return SQUARE_PLAY_STORE_URL;
  return SQUARE_APP_STORE_URL;
}
